"use client";

import * as stylex from "@stylexjs/stylex";
import { CalendarDays, CalendarX2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { copy } from "@/copy";
import { type Breakpoint, layoutSize } from "@/domain/breakpoint";
import type { CalendarEvent, Tag } from "@/domain/schemas";
import { filterScope, matchesFilter } from "@/domain/tags";
import { type Clock, clockAt } from "@/domain/time";
import type { ZoneState } from "@/domain/zoneState";
import "@/theme/tokens.stylex";
import { colors, motion, radii, space } from "@/theme/tokens.stylex";
import { AllDayTiles } from "@/ui/AllDayTile";
import { DayTimeline, type DayTimelineLabels, type TimelineWeather } from "@/ui/DayTimeline";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { Skeleton, SkeletonBlock } from "@/ui/Skeleton";
import { SourceFailure } from "@/ui/SourceFailure";
import { Zone } from "@/ui/Zone";
import { DayList } from "./DayList";
type CalendarDayProps = {
  state: ZoneState<CalendarEvent>;
  toolbar: ReactNode;
  spanLink: ReactNode;
  tags: readonly Tag[];
  filter: ReadonlySet<string>;
  calendarEmpty: boolean;
  day: string;
  now: Date;
  tz: string;
  clock: Clock;
  breakpoint: Breakpoint;
  span24: boolean;
  weather?: TimelineWeather;
  retryIn: number;
  onRetry: () => void;
  onOpenEvent: (event: CalendarEvent) => void;
  onAddEvent: (slot: {
    start: Date;
    end: Date;
  }) => void;
};
type TimelineProps = Omit<CalendarDayProps, "state" | "toolbar" | "calendarEmpty" | "retryIn" | "onRetry"> & {
  events: readonly CalendarEvent[];
};
const LABELS: DayTimelineLabels = {
  ...copy.timeline,
  everyone: copy.events.everyone,
  icons: copy.weather.icon
};
const SKELETON_ROWS = [0, 1, 2];
const TIMELINE_MIN = {
  day: 320,
  full: 760
} as const;
const _temp = {
  k1ekBW: "x1n6d7dw",
  kIyJzY: "x1fmhw06",
  kAMwcw: "x1em5f5i",
  "$$css": true
};
const styles = {
  panel: {
    kzQI83: "x1iyjqo2",
    kmuXW: "x2lah0s",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xddi9bj",
    k7Eaqz: "xeuugli",
    kB7OPa: "x9f619",
    kaIpWk: "xwe7p7i",
    kWkggS: "xh540kj",
    kMwMTN: "x1a11ngh",
    $$css: true
  },
  large: {
    kLKAdn: "x1cnzs8",
    kGO01o: "xx6bls6",
    kZCmMZ: "x106a9eq",
    kwRFfy: "x64bnmy",
    $$css: true
  },
  small: {
    kOIVth: "x1ur044a",
    kLKAdn: "xz9dl7a",
    kg3NbH: "xaope02",
    kGO01o: "xwxc41k",
    $$css: true
  },
  timeline: (minHeight: number) => [_temp, {
    kAzted: minHeight != null ? "x82snj4" : minHeight,
    $$css: true
  }, {
    "--x-minHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(minHeight)
  }],
  emptyTile: {
    kAzted: "x1bq8ba2",
    kWkggS: "x5r8ks9",
    kaIpWk: "xwe7p7i",
    kmVPX3: "xhzry11",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kjj79g: "xl56j7k",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xddi9bj",
    $$css: true
  },
  emptySmall: {
    kAzted: "x2lwn1j",
    kWkggS: "xjbqb8w",
    kLKAdn: "x1q88djf",
    kGO01o: "x18d9i69",
    kg3NbH: "xnjsko4",
    kOIVth: "x1hu4l6k",
    $$css: true
  },
  emptyText: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x17d4w8g",
    $$css: true
  },
  emptyTitle: {
    kGuDYH: "xneq65o",
    k63SB2: "x1v94iar",
    kb6lSQ: "xrg9stw",
    kLWn49: "x1uo3zyz",
    $$css: true
  },
  emptyTitleSmall: {
    kGuDYH: "x1pvqxga",
    kb6lSQ: "x1tup8yo",
    $$css: true
  },
  emptyBody: {
    kGuDYH: "x19d36u7",
    k63SB2: "x15i68ds",
    kLWn49: "xfrs9s4",
    ks0D6T: "x1pfbilc",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  caption: {
    kg3NbH: "x96kwfj",
    $$css: true
  },
  skeleton: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1ur044a",
    kmVPX3: "x1vvitgl",
    $$css: true
  }
};
export function CalendarDay({
  state,
  toolbar,
  calendarEmpty,
  retryIn,
  onRetry,
  ...timeline
}: CalendarDayProps) {
  const small = layoutSize(timeline.breakpoint) === "small";
  return <section {...stylex.props(styles.panel, styles[layoutSize(timeline.breakpoint)])}>
      {toolbar}
      <Zone state={state} loading={<DaySkeleton />} empty={calendarEmpty ? <EmptyCalendar small={small} /> : <DayPlans events={[]} {...timeline} />} failed={<SourceFailure icon={CalendarX2} title={copy.calendar.unavailable} body={copy.zone.failedBody(retryIn)} retryLabel={copy.zone.retry} onRetry={onRetry} />}>
        {(events, updatedAt) => <>
            <DayPlans events={events} {...timeline} />
            {updatedAt && <Eyebrow size="caption" xstyle={styles.caption}>
                {copy.calendar.updated(clockAt(updatedAt, timeline.clock, timeline.tz))}
              </Eyebrow>}
          </>}
      </Zone>
    </section>;
}
function DayPlans(props: TimelineProps) {
  return layoutSize(props.breakpoint) === "small" ? <SmallDay {...props} /> : <Timeline {...props} />;
}
function SmallDay({
  events,
  tags,
  filter,
  day,
  now,
  tz,
  clock,
  weather,
  onOpenEvent,
  onAddEvent
}: TimelineProps) {
  const [earlierOpen, setEarlierOpen] = useState(false);
  const allIds = tags.map(tag => tag.id);
  return <DayList events={events.filter(event => matchesFilter(event, filter, allIds))} tags={tags} day={day} now={now} tz={tz} clock={clock} free={filterScope(filter, allIds)} weather={weather} earlierOpen={earlierOpen} onToggleEarlier={() => setEarlierOpen(open => !open)} onOpenEvent={onOpenEvent} onAddEvent={onAddEvent} />;
}
function Timeline({
  events,
  tags,
  filter,
  day,
  now,
  tz,
  clock,
  breakpoint,
  span24,
  weather,
  spanLink,
  onOpenEvent,
  onAddEvent
}: TimelineProps) {
  const allIds = tags.map(tag => tag.id);
  const shown = events.filter(event => matchesFilter(event, filter, allIds));
  const minHeight = TIMELINE_MIN[span24 ? "full" : "day"];
  return <>
      <AllDayTiles events={shown.filter(event => event.allDay)} tags={tags} label={copy.events.allDay} everyoneLabel={copy.events.everyone} onOpenEvent={onOpenEvent} />
      <DayTimeline plans={shown.filter(event => !event.allDay)} tags={tags} day={day} now={now} tz={tz} clock={clock} variant="calendar" breakpoint={breakpoint} span={span24 ? "24h" : "day"} free={filterScope(filter, allIds)} weather={weather} labels={LABELS} onOpenEvent={onOpenEvent} onOpenMore={onOpenEvent} onAddEvent={onAddEvent} xstyle={styles.timeline(minHeight)} />
      {spanLink}
    </>;
}
function DaySkeleton() {
  return <Skeleton label={copy.calendar.loading} xstyle={styles.skeleton}>
      {SKELETON_ROWS.map(row => <SkeletonBlock key={row} height={44} radius={radii.md} />)}
    </Skeleton>;
}
function EmptyCalendar({
  small
}: {
  small: boolean;
}) {
  return <div {...{
    0: {
      className: "x1bq8ba2 x5r8ks9 xwe7p7i xhzry11 x9f619 x78zum5 xdt5ytf xl56j7k x1cy8zhl xddi9bj"
    },
    1: {
      className: "xwe7p7i xhzry11 x9f619 x78zum5 xdt5ytf xl56j7k x1cy8zhl x2lwn1j xjbqb8w x1q88djf x18d9i69 xnjsko4 x1hu4l6k"
    }
  }[!!small << 0]}>
      <Icon icon={CalendarDays} size={small ? 28 : 32} strokeWidth={1.5} />
      <div className="x78zum5 xdt5ytf x17d4w8g">
        <span {...{
        0: {
          className: "xneq65o x1v94iar xrg9stw x1uo3zyz"
        },
        1: {
          className: "x1v94iar x1uo3zyz x1pvqxga x1tup8yo"
        }
      }[!!small << 0]}>
          {copy.calendar.emptyTitle}
        </span>
        <span className="x19d36u7 x15i68ds xfrs9s4 x1pfbilc x1fzhlzt">{copy.calendar.emptyBody}</span>
      </div>
    </div>;
}